import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Briefcase,
  Users,
  AlertTriangle,
  Clock,
  RefreshCw,
  Search,
  TrendingUp,
  UserMinus,
  Calendar,
  CheckCircle2,
  XCircle,
  Trophy,
  Target,
  ChevronDown,
  ChevronUp,
  Settings2,
  Loader2,
  BellOff,
  Send,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { jobsApi, dashboardApi, getErrorMessage } from '../services/api';
import { toast } from 'sonner';

// Color tokens for the 9 stage segments in the pipeline mini-bar
const STAGE_COLORS = {
  sourced: 'bg-slate-400',
  screening: 'bg-sky-400',
  client_submission: 'bg-violet-500',
  interview_scheduled: 'bg-amber-400',
  interview_selected: 'bg-emerald-400',
  offer_released: 'bg-emerald-600',
  joined: 'bg-teal-700',
  rejected: 'bg-rose-400',
};
const STAGE_ORDER = [
  'sourced', 'screening', 'client_submission',
  'interview_scheduled', 'interview_selected', 'offer_released',
  'joined', 'rejected',
];

// ---------- Silent-recruiter Nudge popover ----------
// Re-uses the closure-health nudge endpoint (in-app notification + Slack)
// so we don't fork a parallel notification surface. Silent recruiters are
// already flagged in this table — the button gives managers a one-click
// way to act on it without bouncing to Slack themselves.
const SilentNudgeButton = ({ recruiter, idx }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const defaultMsg = `Hey ${recruiter?.name?.split(' ')[0] || 'there'} — noticed zero submissions in the past 2 days. Anything blocking you? Let's unblock together.`;
  const [msg, setMsg] = useState(defaultMsg);

  const handleSend = async () => {
    if (!recruiter?.id) return;
    setSending(true);
    try {
      const res = await dashboardApi.nudgeRecruiter(recruiter.id, 'month', msg.trim() || defaultMsg);
      const slackBit = res.data?.slack_notified
        ? ' · Slack ✓'
        : res.data?.slack_configured === false
          ? ' (Slack not configured)'
          : '';
      toast.success(`Nudged ${recruiter.name}${slackBit}`);
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to send nudge');
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setMsg(defaultMsg);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 bg-white hover:bg-amber-100 transition flex-shrink-0"
          data-testid={`ja-rec-nudge-btn-${idx}`}
          aria-label={`Nudge ${recruiter?.name || 'recruiter'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Send className="w-2.5 h-2.5" />
          Nudge
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3 space-y-2"
        data-testid={`ja-rec-nudge-popover-${idx}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-semibold text-slate-800">
          Nudge {recruiter?.name}
        </div>
        <div className="text-[10px] text-slate-500">
          Drops an in-app notification + Slack message (if configured).
        </div>
        <textarea
          className="w-full text-xs rounded border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
          rows={3}
          value={msg}
          onChange={(e) => setMsg(e.target.value.slice(0, 500))}
          placeholder="Add a personal note..."
          data-testid={`ja-rec-nudge-message-${idx}`}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-400">{msg.length}/500</span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={sending}
              data-testid={`ja-rec-nudge-cancel-${idx}`}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending}
              data-testid={`ja-rec-nudge-send-${idx}`}
              className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
            >
              {sending ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="w-3 h-3 mr-1" /> Send nudge
                </>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const KpiTile = ({ label, value, icon: Icon, tone = 'slate', testid, hint }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div
      className={`rounded-lg border ${toneMap[tone]} px-3 py-2.5 flex items-start gap-3`}
      data-testid={testid}
      title={hint}
    >
      <div className="w-9 h-9 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
        <div className="text-2xl font-bold text-slate-900 leading-tight">{value}</div>
        {hint ? <div className="text-[10px] text-slate-500 mt-0.5 truncate">{hint}</div> : null}
      </div>
    </div>
  );
};

// Tiny segmented bar showing pipeline distribution per job
const PipelineBar = ({ pipeline }) => {
  const total = pipeline.total || 0;
  if (total === 0) {
    return <span className="text-[10px] italic text-slate-400">No candidates yet</span>;
  }
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 flex-1 min-w-[60px] max-w-[180px]">
        {STAGE_ORDER.map((s) => {
          const c = pipeline[s] || 0;
          if (c === 0) return null;
          const pct = (c / total) * 100;
          return (
            <div
              key={s}
              className={STAGE_COLORS[s]}
              style={{ width: `${pct}%` }}
              title={`${s.replace(/_/g, ' ')}: ${c}`}
            />
          );
        })}
      </div>
      <span className="text-xs font-mono text-slate-600 flex-shrink-0">{total}</span>
    </div>
  );
};

const RecruiterChips = ({ recruiters, max = 4 }) => {
  if (!recruiters || recruiters.length === 0) {
    return (
      <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200" data-testid="job-row-unassigned">
        Unassigned
      </Badge>
    );
  }
  const shown = recruiters.slice(0, max);
  const more = recruiters.length - shown.length;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((r) => (
        <span
          key={r.id}
          className="w-6 h-6 rounded-full bg-teal-100 border-2 border-white flex items-center justify-center text-[10px] font-semibold text-teal-800"
          title={r.name}
        >
          {r.initials}
        </span>
      ))}
      {more > 0 && (
        <span className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-semibold text-slate-600">
          +{more}
        </span>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    assigned: 'bg-teal-50 text-teal-700 border-teal-200',
    on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${map[status] || 'bg-slate-50 text-slate-600 border-slate-200'} capitalize`}>
      {status?.replace(/_/g, ' ') || 'open'}
    </Badge>
  );
};

// Reusable sortable-header button for the Recruiter Workload table.
// Mirrors the same UX as the Offer vs Joining → By Recruiter table:
//   • idle column → muted ↕ icon
//   • active column → teal text + ↑/↓ icon matching dir
//   • text columns start ascending; numeric columns start descending so
//     the "most interesting first" UX is intuitive.
const RecSortBtn = ({ k, align = 'right', children, sort, setSort }) => {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  const onClick = () => {
    setSort((cur) => {
      if (cur.key !== k) return { key: k, dir: k === 'name' ? 'asc' : 'desc' };
      return { key: k, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
    });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 hover:text-teal-700 transition w-full ${
        align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''
      } ${active ? 'text-teal-700' : ''}`}
      data-testid={`ja-rec-sort-${k}`}
      aria-pressed={active}
      title={`Sort by ${typeof children === 'string' ? children : k}`}
    >
      <span>{children}</span>
      <Icon className={`w-3 h-3 ${active ? '' : 'opacity-40'}`} />
    </button>
  );
};

const JobAllocation = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | unassigned | idle | sla_breached
  const [goalData, setGoalData] = useState(null);
  const [goalLoading, setGoalLoading] = useState(true);
  const [goalCollapsed, setGoalCollapsed] = useState(false);
  // Filter chip on Recruiter Workload: when on, hides anyone who submitted
  // at least one candidate in the past 2 days. Surfaces the "silent"
  // recruiters managers want to follow up on for weekly 1:1s.
  const [silentOnly, setSilentOnly] = useState(false);
  const [bulkGoalOpen, setBulkGoalOpen] = useState(false);
  // Persisted sort state for the Recruiter Workload table. Managers commonly
  // want to find idle recruiters (Closes asc / Jobs asc) or top performers
  // (Closes desc) — so all five columns are sortable. Defaults to Closes-desc
  // which matches the historical server-side ordering.
  const [recSort, setRecSort] = useState(() => {
    try {
      const raw = window.localStorage.getItem('ja-rec-sort');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return { key: 'closures', dir: 'desc' };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('ja-rec-sort', JSON.stringify(recSort));
    } catch (_) { /* ignore */ }
  }, [recSort]);

  const fetchData = async () => {
    setLoading(true);
    setGoalLoading(true);
    // Fire both requests in parallel — they're independent
    const [summary, goals] = await Promise.allSettled([
      jobsApi.allocationSummary(),
      jobsApi.teamGoalProgress(),
    ]);
    if (summary.status === 'fulfilled') setData(summary.value.data);
    else toast.error(getErrorMessage(summary.reason, 'Could not load allocation summary'));
    if (goals.status === 'fulfilled') setGoalData(goals.value.data);
    else setGoalData(null);
    setLoading(false);
    setGoalLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredJobs = useMemo(() => {
    if (!data?.jobs) return [];
    let rows = data.jobs;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (j) =>
          (j.title || '').toLowerCase().includes(q) ||
          (j.client_name || '').toLowerCase().includes(q) ||
          (j.recruiters || []).some((r) => (r.name || '').toLowerCase().includes(q))
      );
    }
    if (filterMode === 'unassigned') rows = rows.filter((j) => j.recruiter_count === 0);
    else if (filterMode === 'idle') rows = rows.filter((j) => j.days_since_last_submission == null || j.days_since_last_submission >= 7);
    else if (filterMode === 'sla_breached') rows = rows.filter((j) => j.sla_remaining_days != null && j.sla_remaining_days < 0);
    return rows;
  }, [data, search, filterMode]);

  const kpis = data?.kpis || {};

  // Sortable + filterable recruiter workload list. Server returns
  // most-closures-first; we re-sort client-side based on the saved sort
  // state so managers can pivot to "who's behind?" without a server
  // round-trip. When `silentOnly` is on we also drop anyone with
  // submissions in the past 2 days (uses backend field `submissions_last_2d`).
  const sortedRecruiters = useMemo(() => {
    const baseRows = data?.recruiters || [];
    const rows = silentOnly
      ? baseRows.filter((r) => (r.submissions_last_2d || 0) === 0)
      : baseRows;
    const extract = {
      name: (r) => (r.name || '').toLowerCase(),
      jobs_assigned: (r) => r.jobs_assigned || 0,
      active_candidates: (r) => r.active_candidates || 0,
      submissions_this_week: (r) => r.submissions_this_week || 0,
      closures: (r) => r.closures_this_month || 0,
    };
    const fn = extract[recSort.key] || extract.closures;
    const sorted = [...rows].sort((a, b) => {
      const va = fn(a);
      const vb = fn(b);
      let cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      // Stable tiebreaker on name to prevent React reshuffle on equal values
      if (cmp === 0) cmp = (a.name || '').localeCompare(b.name || '');
      return recSort.dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [data, recSort, silentOnly]);

  // Live count of "silent" recruiters drives both the badge in the filter
  // chip + the empty-state hint. Computed independently of sortedRecruiters
  // so the chip stays accurate even when the filter is OFF.
  const silentCount = useMemo(
    () => (data?.recruiters || []).filter((r) => (r.submissions_last_2d || 0) === 0).length,
    [data]
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 space-y-4" data-testid="job-allocation-page">
        <header className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-teal-700" />
              Job Allocation Summary
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Single-screen view of every open requisition, who owns it, and recruiter workload across the team.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="ja-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5" data-testid="ja-kpi-strip">
          <KpiTile label="Total Open Jobs" value={kpis.total_open_jobs ?? '—'} icon={Briefcase} tone="slate" testid="ja-kpi-total" />
          <KpiTile label="Unassigned" value={kpis.unassigned_jobs ?? '—'} icon={UserMinus} tone="rose" testid="ja-kpi-unassigned" hint="No recruiter on the job" />
          <KpiTile label="Idle ≥ 7d" value={kpis.idle_jobs ?? '—'} icon={Clock} tone="amber" testid="ja-kpi-idle" hint="No submission in last 7 days" />
          <KpiTile label="SLA Breached" value={kpis.sla_breached_jobs ?? '—'} icon={AlertTriangle} tone="rose" testid="ja-kpi-sla" />
          <KpiTile label="Avg Recruiters / Job" value={kpis.avg_recruiters_per_job ?? '—'} icon={Users} tone="teal" testid="ja-kpi-avg" />
          <KpiTile label="Active Recruiters" value={kpis.total_active_recruiters ?? '—'} icon={TrendingUp} tone="teal" testid="ja-kpi-recruiters" />
          <KpiTile label="Recruiters w/ No Jobs" value={kpis.recruiters_with_no_jobs ?? '—'} icon={UserMinus} tone="amber" testid="ja-kpi-idle-recs" hint="Capacity not deployed" />
        </div>

        {/* ===================== TEAM GOAL PROGRESS WIDGET ===================== */}
        <Card className="overflow-hidden" data-testid="ja-team-goal-card">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-slate-800">Team Goal Progress</h2>
            <span className="text-[10px] text-slate-500 font-mono">
              · monthly closures · {goalData?.days_remaining ?? '—'}d left
            </span>
            {goalData?.team_avg_progress_pct != null && (
              <Badge variant="outline" className="text-[10px]" data-testid="ja-team-avg-progress">
                Team avg {goalData.team_avg_progress_pct}%
              </Badge>
            )}
            {goalData?.effective_default_goal != null && (
              <Badge
                variant="outline"
                className="text-[10px] bg-white border-slate-200 text-slate-600"
                data-testid="ja-team-goal-default-badge"
                title={
                  goalData.org_goal
                    ? 'Organisation default goal'
                    : 'Using application default — no organisation goal set'
                }
              >
                Org default {goalData.effective_default_goal}
                {!goalData.org_goal && <span className="ml-1 text-slate-400">(env)</span>}
              </Badge>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => setBulkGoalOpen(true)}
                disabled={goalLoading && !goalData}
                data-testid="ja-team-goal-bulk-trigger"
                title="Set a monthly closure goal for the whole team"
              >
                <Target className="w-3.5 h-3.5 mr-1" />
                Set team goal
              </Button>
              <button
                type="button"
                onClick={() => setGoalCollapsed(!goalCollapsed)}
                className="text-slate-500 hover:text-slate-800 p-1"
                data-testid="ja-team-goal-toggle"
                aria-label={goalCollapsed ? 'Expand' : 'Collapse'}
              >
                {goalCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {!goalCollapsed && (
            <div className="p-3" data-testid="ja-team-goal-grid">
              {goalLoading && !goalData ? (
                <div className="flex items-center justify-center py-6 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm">Loading goals…</span>
                </div>
              ) : !goalData?.items?.length ? (
                <div className="text-center py-6 text-slate-400 text-sm" data-testid="ja-team-goal-empty">
                  <Target className="w-6 h-6 mx-auto mb-1.5 text-slate-300" />
                  <p>No active recruiters with goals to show.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
                  {goalData.items.map((r, idx) => (
                    <RecruiterGoalCard
                      key={r.user_id}
                      recruiter={r}
                      idx={idx}
                      onOpen={() => navigate(`/my-performance?user_id=${r.user_id}`)}
                      onSaved={fetchData}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Bulk "Set team goal" dialog — renders only when open, stays close to parent state */}
        <BulkTeamGoalDialog
          open={bulkGoalOpen}
          onOpenChange={setBulkGoalOpen}
          goalData={goalData}
          onSaved={fetchData}
        />

        {/* Two-column layout: jobs (left, wide) + recruiter load (right).
            Both cards are height-bounded to viewport so their internal
            ScrollArea engages — without this they grew to fit content and
            the user had to scroll the whole page to see anything. */}
        <div className="grid grid-cols-12 gap-4">
          {/* JOBS TABLE */}
          <Card
            className="col-span-12 xl:col-span-8 flex flex-col h-[calc(100vh-220px)] min-h-[520px]"
            data-testid="ja-jobs-card"
          >
            <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-slate-800">
                Job Allocation
                <span className="ml-1.5 text-xs font-mono text-slate-500">{filteredJobs.length}</span>
              </h2>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    placeholder="Search title / client / recruiter…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8 text-xs pl-8 w-[260px]"
                    data-testid="ja-jobs-search"
                  />
                </div>
                {[
                  { key: 'all', label: 'All' },
                  { key: 'unassigned', label: 'Unassigned' },
                  { key: 'idle', label: 'Idle' },
                  { key: 'sla_breached', label: 'SLA' },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setFilterMode(m.key)}
                    data-testid={`ja-filter-${m.key}`}
                    className={`text-xs px-2 py-1 rounded-md border transition ${
                      filterMode === m.key
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <ScrollArea className="flex-1">
              {loading && !data ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="text-center py-16 text-slate-400 text-sm" data-testid="ja-jobs-empty">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No jobs match the current filter.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                      <th className="text-left px-3 py-2">Job</th>
                      <th className="text-left px-3 py-2 hidden md:table-cell">Status</th>
                      <th className="text-left px-3 py-2 hidden lg:table-cell">Age / SLA</th>
                      <th className="text-left px-3 py-2">Recruiters</th>
                      <th className="text-left px-3 py-2 hidden md:table-cell">Pipeline</th>
                      <th className="text-right px-3 py-2 hidden lg:table-cell">Last Submission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredJobs.map((j, idx) => {
                      const slaBreached = j.sla_remaining_days != null && j.sla_remaining_days < 0;
                      const idle = j.days_since_last_submission == null || j.days_since_last_submission >= 7;
                      return (
                        <tr key={j.id} className="hover:bg-slate-50/50 transition" data-testid={`ja-job-row-${idx}`}>
                          <td className="px-3 py-2.5">
                            <div className="font-semibold text-slate-900 truncate max-w-[260px]" title={j.title}>{j.title || 'Untitled'}</div>
                            <div className="text-xs text-slate-500 truncate max-w-[260px]" title={j.client_name}>{j.client_name || '—'}</div>
                          </td>
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            <StatusBadge status={j.status} />
                          </td>
                          <td className="px-3 py-2.5 hidden lg:table-cell">
                            <div className="text-xs text-slate-700">{j.age_days != null ? `${j.age_days}d old` : '—'}</div>
                            {j.sla_days != null ? (
                              <div className={`text-[10px] font-medium ${slaBreached ? 'text-rose-600' : 'text-slate-500'}`}>
                                SLA {j.sla_days}d {j.sla_remaining_days != null ? `(${slaBreached ? `${Math.abs(j.sla_remaining_days)}d over` : `${j.sla_remaining_days}d left`})` : ''}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5">
                            <RecruiterChips recruiters={j.recruiters} />
                          </td>
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            <PipelineBar pipeline={j.pipeline} />
                          </td>
                          <td className="px-3 py-2.5 hidden lg:table-cell text-right">
                            {j.days_since_last_submission == null ? (
                              <span className="text-[10px] italic text-slate-400">Never</span>
                            ) : (
                              <span className={`text-xs ${idle ? 'text-amber-600 font-semibold' : 'text-slate-600'}`}>
                                {j.days_since_last_submission}d ago
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </ScrollArea>
          </Card>

          {/* RECRUITER LOAD */}
          <Card
            className="col-span-12 xl:col-span-4 flex flex-col h-[calc(100vh-220px)] min-h-[520px]"
            data-testid="ja-recruiters-card"
          >
            <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap">
              <Users className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-800">Recruiter Workload</h2>
              <span className="text-xs font-mono text-slate-500" data-testid="ja-rec-count">
                {data?.recruiters?.length || 0}
              </span>
              {/* Silent-2d filter: surfaces recruiters with zero submissions
                  in the past ~48h. Click to toggle; amber tone when active so
                  the manager knows the table is now scoped. */}
              <button
                type="button"
                onClick={() => setSilentOnly((v) => !v)}
                data-testid="ja-rec-silent-chip"
                aria-pressed={silentOnly}
                title="Show only recruiters with zero submissions in the last 2 days"
                className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border transition ${
                  silentOnly
                    ? 'bg-amber-100 border-amber-300 text-amber-800 shadow-sm'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <BellOff className="w-3 h-3" />
                Silent 2d
                <span className={`ml-0.5 text-[10px] rounded-full px-1.5 py-0.5 ${
                  silentOnly ? 'bg-white text-amber-800' : 'bg-amber-50 text-amber-700'
                }`}>
                  {silentCount}
                </span>
              </button>
            </div>
            <ScrollArea className="flex-1">
              {loading && !data ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : (data?.recruiters || []).length === 0 ? (
                <div className="text-center py-16 text-slate-400 text-sm" data-testid="ja-rec-empty">
                  <p>No active recruiters in this org.</p>
                </div>
              ) : (
                <table className="w-full min-w-[420px] text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                      <th className="text-left px-3 py-2 min-w-[160px]">
                        <RecSortBtn k="name" align="left" sort={recSort} setSort={setRecSort}>Recruiter</RecSortBtn>
                      </th>
                      <th className="text-right px-2 py-2" title="Jobs assigned">
                        <RecSortBtn k="jobs_assigned" sort={recSort} setSort={setRecSort}>Jobs</RecSortBtn>
                      </th>
                      <th className="text-right px-2 py-2" title="Active candidates in pipeline">
                        <RecSortBtn k="active_candidates" sort={recSort} setSort={setRecSort}>Active</RecSortBtn>
                      </th>
                      <th className="text-right px-2 py-2 hidden lg:table-cell" title="Submissions this week">
                        <RecSortBtn k="submissions_this_week" sort={recSort} setSort={setRecSort}>7d Subs</RecSortBtn>
                      </th>
                      <th className="text-right px-2 py-2 pr-3" title="Closures this month">
                        <RecSortBtn k="closures" sort={recSort} setSort={setRecSort}>Closes</RecSortBtn>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedRecruiters.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-10 text-center text-xs text-slate-400 italic">
                          {silentOnly
                            ? '🎉 Every recruiter submitted at least one candidate in the past 2 days.'
                            : 'No recruiters match.'}
                        </td>
                      </tr>
                    ) : null}
                    {sortedRecruiters.map((r, idx) => {
                      const noJobs = r.jobs_assigned === 0;
                      const silent = (r.submissions_last_2d || 0) === 0;
                      return (
                        <tr key={r.id} className="hover:bg-slate-50/50" data-testid={`ja-rec-row-${idx}`}>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-[10px] font-bold text-teal-800 flex-shrink-0">
                                {r.initials}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold text-slate-900 truncate flex items-center gap-1.5" title={r.name}>
                                  <span className="truncate">{r.name}</span>
                                  {silent ? (
                                    <>
                                      <span
                                        className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0"
                                        title="Zero submissions in the last 2 days"
                                        data-testid={`ja-rec-silent-${idx}`}
                                      >
                                        <BellOff className="w-2.5 h-2.5" />
                                        Silent
                                      </span>
                                      <SilentNudgeButton recruiter={r} idx={idx} />
                                    </>
                                  ) : null}
                                </div>
                                <div className="text-[10px] text-slate-500 truncate" title={r.email}>{r.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className={`px-2 py-2.5 text-right text-xs font-semibold ${noJobs ? 'text-rose-600' : 'text-slate-700'}`}>
                            {r.jobs_assigned}
                            {noJobs ? <XCircle className="w-3 h-3 inline ml-1 -mt-0.5" /> : null}
                          </td>
                          <td className="px-2 py-2.5 text-right text-xs text-slate-700">{r.active_candidates}</td>
                          <td className="px-2 py-2.5 text-right text-xs text-slate-700 hidden lg:table-cell">{r.submissions_this_week}</td>
                          <td className="px-2 py-2.5 pr-3 text-right text-xs font-semibold text-emerald-700">{r.closures_this_month}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </ScrollArea>
            {data?.generated_at ? (
              <div className="px-4 py-2 border-t text-[10px] text-slate-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Generated {new Date(data.generated_at).toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </div>
            ) : null}
          </Card>
        </div>
    </div>
  );
};

// ---------- Per-recruiter goal card with inline "Set goal" popover ----------
const RecruiterGoalCard = ({ recruiter: r, idx, onOpen, onSaved }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draft, setDraft] = useState(String(r.goal ?? ''));
  const [saving, setSaving] = useState(false);

  // When the popover opens, sync draft with current goal (in case it changed after last save)
  useEffect(() => {
    if (popoverOpen) setDraft(String(r.goal ?? ''));
  }, [popoverOpen, r.goal]);

  const variantStyle = {
    achieved: { card: 'border-emerald-200 bg-emerald-50/40', bar: 'bg-emerald-500', label: 'text-emerald-700', icon: CheckCircle2 },
    ontrack: { card: 'border-teal-200 bg-teal-50/40', bar: 'bg-teal-500', label: 'text-teal-700', icon: TrendingUp },
    cold: { card: 'border-amber-200 bg-amber-50/40', bar: 'bg-amber-400', label: 'text-amber-700', icon: Target },
    behind: { card: 'border-rose-200 bg-rose-50/40', bar: 'bg-rose-500', label: 'text-rose-700', icon: AlertTriangle },
  }[r.variant] || { card: 'border-slate-200 bg-slate-50', bar: 'bg-slate-400', label: 'text-slate-700', icon: Target };
  const VIcon = variantStyle.icon;

  const handleSave = async () => {
    const n = draft.trim() === '' ? null : Number(draft);
    if (n !== null && (!Number.isFinite(n) || n < 0 || n > 500)) {
      toast.error('Goal must be a whole number between 0 and 500.');
      return;
    }
    setSaving(true);
    try {
      await jobsApi.setRecruiterGoal(r.user_id, n);
      toast.success(n ? `Goal set to ${n}` : 'Goal reset to org default');
      setPopoverOpen(false);
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save goal'));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await jobsApi.setRecruiterGoal(r.user_id, null);
      toast.success('Goal reset to org default');
      setPopoverOpen(false);
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not clear goal'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`relative rounded-md border ${variantStyle.card} px-3 py-2 hover:shadow-sm transition group`}
      data-testid={`ja-goal-row-${idx}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        title={`Click to view ${r.name}'s performance`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center text-[10px] font-bold text-slate-700 flex-shrink-0">
            {r.initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-slate-900 truncate" title={r.name}>{r.name}</div>
            <div className="text-[10px] text-slate-500 truncate">{r.email || ''}</div>
          </div>
          <VIcon className={`w-3.5 h-3.5 ${variantStyle.label} flex-shrink-0`} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-white/70 rounded-full overflow-hidden">
            <div className={`h-full ${variantStyle.bar} transition-all`} style={{ width: `${Math.min(100, r.progress_pct)}%` }} />
          </div>
          <span className={`text-[10px] font-mono font-semibold ${variantStyle.label} flex-shrink-0`}>
            {r.closures_done}/{r.goal}
          </span>
        </div>
        <div className={`text-[10px] mt-1 ${variantStyle.label}`} data-testid={`ja-goal-status-${idx}`}>
          {r.variant === 'achieved' && '🎯 Goal achieved!'}
          {r.variant === 'ontrack' && `On track — ${r.behind} to go`}
          {r.variant === 'cold' && `Get the first one home — ${r.behind} closures needed`}
          {r.variant === 'behind' && `${r.behind} closure${r.behind === 1 ? '' : 's'} behind goal`}
        </div>
      </button>

      {/* Set-goal popover trigger — absolutely positioned so it doesn't steal the card click */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            data-testid={`ja-goal-set-trigger-${idx}`}
            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition p-1 rounded hover:bg-white/80 text-slate-500 hover:text-slate-800"
            aria-label={`Set goal for ${r.name}`}
            title="Set monthly closure goal"
          >
            <Settings2 className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-3"
          align="end"
          onClick={(e) => e.stopPropagation()}
          data-testid={`ja-goal-set-popover-${idx}`}
        >
          <div className="space-y-2">
            <div>
              <div className="text-xs font-semibold text-slate-800">{r.name}</div>
              <div className="text-[10px] text-slate-500">Monthly closure goal</div>
            </div>
            <Label htmlFor={`goal-input-${idx}`} className="text-[10px] uppercase tracking-wide text-slate-500">
              Target closures per month
            </Label>
            <Input
              id={`goal-input-${idx}`}
              type="number"
              min={0}
              max={500}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              placeholder="5"
              disabled={saving}
              className="h-9"
              data-testid={`ja-goal-set-input-${idx}`}
            />
            <p className="text-[10px] text-slate-400">Leave blank or 0 to use the organisation default.</p>
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-8 flex-1"
                data-testid={`ja-goal-set-save-${idx}`}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleClear}
                disabled={saving}
                className="h-8"
                data-testid={`ja-goal-set-clear-${idx}`}
                title="Revert to organisation default"
              >
                Reset
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default JobAllocation;

// ---------- Bulk "Set team goal" dialog ----------
// Lets managers push one numeric goal to the whole team in a single click.
// Two modes (checkbox):
//   (off) — only recruiters currently on the org/env default get the new number.
//           Recruiters with a personal override keep their custom value.
//   (on)  — wipes every recruiter's personal override so everyone lands on the new default.
// Leaving the field blank / 0 clears the org default → falls back to env default.
const BulkTeamGoalDialog = ({ open, onOpenChange, goalData, onSaved }) => {
  const [draft, setDraft] = useState('');
  const [overridePersonal, setOverridePersonal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sync local state with the live org default every time the dialog opens
  useEffect(() => {
    if (open) {
      setDraft(String(goalData?.org_goal ?? ''));
      setOverridePersonal(false);
    }
  }, [open, goalData?.org_goal]);

  const affected = goalData?.affected_by_bulk ?? 0;
  const withOverride = goalData?.with_personal_override ?? 0;
  const totalRecruiters = (goalData?.items?.length) ?? 0;

  const handleSave = async () => {
    const raw = draft.trim();
    const n = raw === '' ? null : Number(raw);
    if (n !== null && (!Number.isFinite(n) || n < 0 || n > 500 || !Number.isInteger(n))) {
      toast.error('Goal must be a whole number between 0 and 500.');
      return;
    }
    setSaving(true);
    try {
      const res = await jobsApi.setOrgGoal(n, overridePersonal);
      const cleared = res?.data?.cleared_overrides || 0;
      if (n == null) {
        toast.success(
          overridePersonal
            ? `Org goal cleared · ${cleared} personal overrides removed`
            : 'Org goal cleared — team falls back to app default'
        );
      } else {
        toast.success(
          overridePersonal
            ? `Team goal set to ${n} · ${cleared} personal overrides removed`
            : `Team goal set to ${n} — applies to recruiters on the default`
        );
      }
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save team goal'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ja-team-goal-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-600" /> Set team goal
          </DialogTitle>
          <DialogDescription>
            Apply a single monthly closure goal across your recruiting team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <Label htmlFor="bulk-goal-input" className="text-xs uppercase tracking-wide text-slate-500">
              Target closures per month
            </Label>
            <Input
              id="bulk-goal-input"
              type="number"
              min={0}
              max={500}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              placeholder="e.g. 5"
              disabled={saving}
              className="h-10 mt-1"
              data-testid="ja-team-goal-bulk-input"
              autoFocus
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Leave blank or 0 to clear the org default — recruiters fall back to the
              application default ({goalData?.env_goal ?? '—'}).
            </p>
          </div>

          <label
            className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 cursor-pointer hover:bg-slate-100 transition"
            data-testid="ja-team-goal-bulk-override-row"
          >
            <Checkbox
              checked={overridePersonal}
              onCheckedChange={(v) => setOverridePersonal(Boolean(v))}
              disabled={saving}
              data-testid="ja-team-goal-bulk-override-checkbox"
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-800">
                Override every recruiter's personal goal
              </div>
              <div className="text-[11px] text-slate-500 leading-snug">
                {withOverride > 0
                  ? `Currently, ${withOverride} recruiter${withOverride === 1 ? ' has a personal' : 's have personal'} override${withOverride === 1 ? '' : 's'}. Ticking this resets ${withOverride === 1 ? 'it' : 'them'} so the whole team lands on the new goal.`
                  : 'No recruiters have a personal override right now — this is a safety net for future changes.'}
              </div>
            </div>
          </label>

          <div
            className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-md px-3 py-2"
            data-testid="ja-team-goal-bulk-summary"
          >
            {overridePersonal ? (
              <span>
                Will affect <strong>{totalRecruiters}</strong> recruiter{totalRecruiters === 1 ? '' : 's'} (everyone).
              </span>
            ) : (
              <span>
                Will affect <strong>{affected}</strong> of {totalRecruiters} recruiter{totalRecruiters === 1 ? '' : 's'}
                {withOverride > 0 ? ` — ${withOverride} with a personal override keep${withOverride === 1 ? 's' : ''} their current goal.` : '.'}
              </span>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="ja-team-goal-bulk-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="ja-team-goal-bulk-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Apply goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
